import React from 'react';
import {useTranslation} from 'react-i18next';
import type {ActionMeta} from 'react-select';
import {QueriedSearchingSelectProps} from './QueriedSearchingSelect';
import {SelectOption} from '@chekin/core';
import {
  CustomIcon,
  MobileSelectModal,
  SelectIconWrapper,
  SelectRefType,
} from '@guestapp/old-ui-kit';
import {QueriedInfiniteScrollSelect} from '../QueriedInfiniteScrollSelect';
import {useLockScrollBody, useModalWithHistoryControls} from '@chekin/guestapp-ui-kit';

const ListSizes = {
  height: 400,
  itemSize: 46,
};

const MobileSearchingSelectInternal = <T, V, L>(
  {trigger, onChange, ...props}: QueriedSearchingSelectProps<T, V, L>,
  ref: React.ForwardedRef<SelectRefType<T, V, L>>,
) => {
  const {t} = useTranslation();
  const {isOpen, closeModal, toggleModal} = useModalWithHistoryControls({
    key: props.name || '',
  });
  useLockScrollBody(isOpen);

  const handleChange = (
    option: SelectOption<T, V, L> | null,
    info: ActionMeta<SelectOption<T, V, L>>,
  ) => {
    closeModal();
    onChange?.(option, info);
  };

  return (
    <>
      {trigger?.(isOpen, toggleModal)}
      <MobileSelectModal
        visible={isOpen}
        onClose={closeModal}
        mode="mobile-slide"
        pressToTheTop
      >
        {isOpen && (
          <QueriedInfiniteScrollSelect
            ref={ref}
            menuIsOpen
            isMenuOpen
            isSearchable
            mobileVersion
            listAutoSizer
            forcedSearchMode
            defaultMenuIsOpen
            label={null}
            components={{LoadingIndicator: undefined, DropdownIndicator: null}}
            noOptionsMessage={() => t('no_matches_found')}
            listSizes={ListSizes}
            onChange={handleChange}
            minSearchQueryLength={0}
            controlShouldRenderValue={false}
            backspaceRemovesValue={false}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            tabSelectsValue={false}
            isClearable={false}
            leftIcon={
              <SelectIconWrapper>
                <CustomIcon size={16} color={'#9F9FBF'} name={'magnifierGray'} />
              </SelectIconWrapper>
            }
            {...props}
          />
        )}
      </MobileSelectModal>
    </>
  );
};

const MobileQueriedSearchingSelect = React.forwardRef(MobileSearchingSelectInternal) as <
  T,
  V,
  L,
>(
  props: QueriedSearchingSelectProps<T, V, L> & {
    ref?: React.ForwardedRef<SelectRefType<T, V, L>>;
  },
) => ReturnType<typeof MobileSearchingSelectInternal>;

export {MobileQueriedSearchingSelect};
